// src/pages/property-details/index.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

// Import components
import ImageGallery from './components/ImageGallery';
import PropertyOverview from './components/PropertyOverview';
import PropertyTabs from './components/PropertyTabs';
import ContactForm from './components/ContactForm';
import SimilarProperties from './components/SimilarProperties';
import LoadingState from './components/LoadingState';
import MakeOfferSection from './components/MakeOfferSection';
import { apiRequest } from '../../services/apiClient';
import { createMarketBid } from '../../services/bidApi';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import HoverTooltip from '../../components/ui/HoverTooltip';

const PropertyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { success, warning, info, error: toastError } = useToast();
  const { isAuthenticated, role, user } = useAuth();
  const { confirm } = useConfirm();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [showContactForm, setShowContactForm] = useState(false);
  const isMarketUser = `${role || ''}`.toUpperCase() === 'MARKET_USER';
  const postLoginAction = location?.state?.postLoginAction;
  const shouldOpenContactFromState =
    location?.state?.openContactModal || postLoginAction?.type === 'open-contact';

  const propertyId = searchParams?.get('id');
  const isOwnProduct = Boolean(user?.id && property?.seller?.id && user.id === property.seller.id);
  const marketActionTooltip = isOwnProduct
    ? 'Bu ürün size ait. Kendinizle iletişime geçemezsiniz.'
    : 'Sadece pazaryeri kullanıcıları bu işlemi yapabilir.';
  const ownProductBidTooltip = 'Bu ürün size ait. Kendi ürününüze teklif gönderemezsiniz.';
  const ownProductContactTooltip = 'Bu ürün size ait. Kendinizle iletişime geçemezsiniz.';
  const ownProductOrderTooltip = 'Bu ürün size ait. Kendi ürününüz için sipariş oluşturamazsınız.';
  const isMarketActionDisabled = !isAuthenticated || !isMarketUser || isOwnProduct;

  useEffect(() => {
    let cancelled = false;

    const fetchProperty = async () => {
      setLoading(true);

      if (!propertyId) {
        setProperty(null);
        setSimilarProperties([]);
        setLoading(false);
        return;
      }

      try {
        const product = await apiRequest(`/products/${propertyId}`);

        if (cancelled) {
          return;
        }

        setProperty(product);
        setIsSaved(Boolean(product?.isSaved));

        const query = new URLSearchParams();
        if (product?.category && product.category !== 'general') {
          query.set('category', product.category);
        }
        if (product?.productType && product.productType !== 'general') {
          query.set('productType', product.productType);
        }
        query.set('pageSize', '4');

        const similarResponse = await apiRequest(`/products?${query.toString()}`);

        if (cancelled) {
          return;
        }

        const similarItems = Array.isArray(similarResponse)
          ? similarResponse
          : similarResponse?.items || [];

        const filteredSimilar = similarItems
          .filter((item) => item?.id !== product?.id)
          .slice(0, 3);

        setSimilarProperties(filteredSimilar);
      } catch (error) {
        console.error('Error fetching product details:', error);
        if (!cancelled) {
          setProperty(null);
          setSimilarProperties([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProperty();

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const handleSave = () => {
    setIsSaved(!isSaved);
    // In real app, sync with backend
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title,
        text: `Bu ürünü incele: ${property?.title}`,
        url: window.location?.href
      });
    } else {
      // Fallback to copy to clipboard
      navigator.clipboard?.writeText(window.location?.href);
    }
  };

  const ensureMarketUserAccess = ({
    loginMessage,
    nonMarketUserMessage,
    postLoginAction: nextPostLoginAction,
  } = {}) => {
    if (!isAuthenticated) {
      info(loginMessage || 'Bu işlem için pazaryeri hesabı ile giriş yapmalısınız.');
      navigate('/login', {
        replace: false,
        state: {
          from: `${location.pathname}${location.search}`,
          postLoginAction: nextPostLoginAction,
        },
      });
      return false;
    }

    if (!isMarketUser) {
      warning(nonMarketUserMessage || 'Bu işlem sadece pazaryeri kullanıcıları tarafından yapılabilir.');
      return false;
    }

    if (isOwnProduct) {
      warning('Bu ürün size ait. Bu işlem kendi ürününüzde kullanılamaz.');
      return false;
    }

    return true;
  };

  const ensureMarketUserAccessWithLoginPrompt = async ({
    loginMessage,
    nonMarketUserMessage,
    postLoginAction: nextPostLoginAction,
  } = {}) => {
    if (!isAuthenticated) {
      const shouldGoToLogin = await confirm({
        title: 'Pazaryeri Girişi Gerekli',
        message: loginMessage || 'Satıcıya ulaşmak için pazaryeri hesabı ile giriş yapmalısınız.',
        confirmText: 'Giriş Yap',
        cancelText: 'Vazgeç',
        tone: 'warning',
      });

      if (shouldGoToLogin) {
        navigate('/login', {
          replace: false,
          state: {
            from: `${location.pathname}${location.search}`,
            postLoginAction: nextPostLoginAction,
          },
        });
      }

      return false;
    }

    if (!isMarketUser) {
      warning(nonMarketUserMessage || 'Bu işlem sadece pazaryeri kullanıcıları tarafından yapılabilir.');
      return false;
    }

    if (isOwnProduct) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!shouldOpenContactFromState || !propertyId) {
      return;
    }

    const matchesProduct =
      !postLoginAction?.productId || postLoginAction.productId === propertyId;

    if (!matchesProduct) {
      return;
    }

    if (isOwnProduct) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
      return;
    }

    if (
      !ensureMarketUserAccess({
        loginMessage: 'Satıcıya ulaşmak için pazaryeri hesabı ile giriş yapmalısınız.',
        nonMarketUserMessage: 'Satıcıya ulaşma sadece pazaryeri kullanıcıları tarafından yapılabilir.',
        postLoginAction: { type: 'open-contact', productId },
      })
    ) {
      return;
    }

    setShowContactForm(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [
    isAuthenticated,
    isMarketUser,
    isOwnProduct,
    location.pathname,
    location.search,
    navigate,
    postLoginAction?.productId,
    propertyId,
    shouldOpenContactFromState,
  ]);

  const handleOfferSubmit = async (offerData) => {
    if (isOwnProduct) {
      warning('Bu ürün size ait. Kendi ürününüze teklif gönderemezsiniz.');
      return;
    }

    if (property?.openToOffer === false) {
      warning('Bu ürün teklife açık değil.');
      return;
    }

    if (
      !ensureMarketUserAccess({
        loginMessage: 'Teklif göndermek için pazaryeri hesabı ile giriş yapmalısınız.',
      })
    ) {
      return;
    }

    try {
      const createdBid = await createMarketBid({
        productId: offerData?.productId,
        amount: offerData?.amount,
        quantity: offerData?.quantity,
        note: offerData?.message || undefined,
      });

      const nextBid = {
        id: createdBid?.id || `${Date.now()}`,
        buyerId: createdBid?.buyer?.id || user?.id || null,
        amount: Number(createdBid?.amount || offerData?.amount || 0),
        quantity: Number(createdBid?.quantity || offerData?.quantity || 0),
        acceptedQuantity: createdBid?.acceptedQuantity || null,
        bidder: createdBid?.buyer?.fullName || createdBid?.buyer?.email || 'Kullanıcı',
        status: createdBid?.status || 'PENDING',
        timestamp: createdBid?.createdAt || new Date().toISOString(),
      };

      setProperty((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          currentBids: [nextBid, ...(prev.currentBids || [])],
        };
      });

      success('Teklif başarıyla gönderildi.');
    } catch (error) {
      toastError(error?.message || 'Teklif gönderilemedi.');
      throw error;
    }
  };

  const handlePlaceOrder = () => {
    if (isOwnProduct) {
      warning('Bu ürün size ait. Kendi ürününüz için sipariş oluşturamazsınız.');
      return;
    }

    if (
      !ensureMarketUserAccess({
        loginMessage: 'Sipariş vermek için pazaryeri hesabı ile giriş yapmalısınız.',
      })
    ) {
      return;
    }

    success('Sipariş akışı bir sonraki adımda bağlanacak.');
  };

  const handleContactSeller = async () => {
    if (isOwnProduct) {
      warning('Bu ürün size ait. Kendinizle iletişime geçemezsiniz.');
      return;
    }

    if (
      !(await ensureMarketUserAccessWithLoginPrompt({
        loginMessage: 'Satıcıya ulaşmak için pazaryeri hesabı ile giriş yapmalısınız.',
        nonMarketUserMessage: 'Satıcıya ulaşma sadece pazaryeri kullanıcıları tarafından yapılabilir.',
        postLoginAction: { type: 'open-contact', productId: propertyId },
      }))
    ) {
      return;
    }

    setShowContactForm(true);
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Home', path: '/homepage' },
      { label: 'Products', path: '/property-listings' },
      { label: property?.title || 'Product Details', path: null }
    ];
    return breadcrumbs;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <LoadingState />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <Icon name="Package" size={64} className="text-secondary mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                Product Not Found
              </h1>
              <p className="text-text-secondary mb-6">
                The agricultural product you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/property-listings"
                className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-700 transition-all duration-200"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Products</span>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 lg:pt-18">
        {/* Breadcrumb */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center space-x-2 text-sm">
              {getBreadcrumbs()?.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Icon name="ChevronRight" size={14} className="text-text-secondary" />
                  )}
                  {crumb?.path ? (
                    <Link
                      to={crumb?.path}
                      className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                    >
                      {crumb?.label}
                    </Link>
                  ) : (
                    <span className="text-text-primary font-medium truncate">{crumb?.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Actions Bar */}
        <div className="lg:hidden bg-surface border-b border-border sticky top-16 z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSave}
                className={`p-2 rounded-md transition-all duration-200 ${
                  isSaved 
                    ? 'bg-error text-white' :'bg-secondary-100 text-text-secondary hover:bg-error hover:text-white'
                }`}
              >
                <Icon name="Heart" size={18} fill={isSaved ? "currentColor" : "none"} />
              </button>
              <button
                onClick={handleShare}
                className="p-2 bg-secondary-100 text-text-secondary rounded-md hover:bg-secondary-200 transition-all duration-200"
              >
                <Icon name="Share" size={18} />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <HoverTooltip
                content={isMarketActionDisabled ? (isOwnProduct ? ownProductContactTooltip : marketActionTooltip) : undefined}
                className="inline-flex"
              >
                <button
                  onClick={handleContactSeller}
                  disabled={isMarketActionDisabled}
                  aria-disabled={isMarketActionDisabled}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    isMarketActionDisabled
                      ? 'border border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                      : 'bg-accent text-white hover:bg-accent-600'
                  }`}
                >
                  Contact Seller
                </button>
              </HoverTooltip>
              <HoverTooltip
                content={isMarketActionDisabled ? (isOwnProduct ? ownProductOrderTooltip : marketActionTooltip) : undefined}
                className="inline-flex"
              >
                <button
                  onClick={handlePlaceOrder}
                  disabled={isMarketActionDisabled}
                  aria-disabled={isMarketActionDisabled}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    isMarketActionDisabled
                      ? 'border border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary-700'
                  }`}
                >
                  Place Order
                </button>
              </HoverTooltip>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <ImageGallery 
                images={property?.images}
                title={property?.title}
                virtualTour={property?.virtualTour}
                video={property?.video}
              />

              {/* Property Overview */}
              <PropertyOverview 
                property={property}
                isSaved={isSaved}
                onSave={handleSave}
                onShare={handleShare}
                onContact={handleContactSeller}
                isMarketActionDisabled={isMarketActionDisabled}
                marketActionTooltip={marketActionTooltip}
              />

              {/* Property Tabs */}
              <PropertyTabs 
                property={property}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                currentUserId={user?.id}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Seller Contact Card */}
              <div className="card p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="relative">
                    <Image
                      src={property?.seller?.avatar}
                      alt={property?.seller?.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-success text-white rounded-full p-1">
                      <Icon name="Tractor" size={12} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-text-primary">{property?.seller?.name}</h3>
                    <p className="text-sm text-text-secondary">Farm Owner & Producer</p>
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="flex items-center">
                        {[...Array(5)]?.map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={i < Math.floor(property?.seller?.rating) ? 'text-warning fill-current' : 'text-secondary-300'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-text-secondary">
                        {property?.seller?.rating} ({property?.seller?.reviewsCount} reviews)
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-success-50 border border-success-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span className="text-sm font-medium text-success-700">Certified Organic Producer</span>
                  </div>
                </div>
                
                <p className="text-sm text-text-secondary mb-4">
                  {property?.seller?.bio}
                </p>
                
                <div className="space-y-3">
                  <HoverTooltip
                    content={isMarketActionDisabled ? (isOwnProduct ? ownProductContactTooltip : marketActionTooltip) : undefined}
                    className="block w-full"
                  >
                    <button
                      onClick={handleContactSeller}
                      disabled={isMarketActionDisabled}
                      aria-disabled={isMarketActionDisabled}
                      className={`w-full flex items-center justify-center space-x-2 py-3 rounded-md transition-all duration-200 ${
                        isMarketActionDisabled
                          ? 'border border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                          : 'bg-primary text-white hover:bg-primary-700'
                      }`}
                    >
                      <Icon name="MessageCircle" size={16} />
                      <span>Contact Farmer</span>
                    </button>
                  </HoverTooltip>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <HoverTooltip
                      content={isOwnProduct ? ownProductContactTooltip : undefined}
                      className="block w-full"
                    >
                      <button
                        disabled={isOwnProduct}
                        className={`flex items-center justify-center space-x-2 py-2 rounded-md transition-all duration-200 ${
                          isOwnProduct
                            ? 'bg-secondary-200 text-text-secondary cursor-not-allowed'
                            : 'bg-accent-100 text-accent-600 hover:bg-accent hover:text-white'
                        }`}
                      >
                        <Icon name="Phone" size={16} />
                        <span className="text-sm">Call</span>
                      </button>
                    </HoverTooltip>
                    <HoverTooltip
                      content={isMarketActionDisabled ? (isOwnProduct ? ownProductOrderTooltip : marketActionTooltip) : undefined}
                      className="block w-full"
                    >
                      <button
                        onClick={handlePlaceOrder}
                        disabled={isMarketActionDisabled}
                        aria-disabled={isMarketActionDisabled}
                        className={`flex items-center justify-center space-x-2 py-2 rounded-md transition-all duration-200 ${
                          isMarketActionDisabled
                            ? 'border border-primary bg-primary text-white opacity-[0.65] cursor-not-allowed'
                            : 'bg-secondary-100 text-text-secondary hover:bg-secondary-200'
                        }`}
                      >
                        <Icon name="Truck" size={16} />
                        <span className="text-sm">Order</span>
                      </button>
                    </HoverTooltip>
                  </div>
                </div>
              </div>

              {/* Make Offer Section */}
              <MakeOfferSection 
                property={property} 
                onOfferSubmit={handleOfferSubmit}
                canSubmitOffer={isAuthenticated && isMarketUser}
                isOfferOpen={property?.openToOffer !== false}
                isOwnProduct={isOwnProduct}
                ownProductBidTooltip={ownProductBidTooltip}
                onRequireMarketUser={ensureMarketUserAccess}
                currentUserId={user?.id}
              />
            </div>
          </div>

          {/* Similar Properties */}
          <div className="mt-12">
            <SimilarProperties properties={similarProperties} />
          </div>
        </div>
      </main>
      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm
          property={property}
          agent={property?.seller}
          onClose={() => setShowContactForm(false)}
        />
      )}
    </div>
  );
};

export default PropertyDetails;
